import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Col,
  Row,
} from "reactstrap";
import { AlternativePagination } from "../../components/Common/Table";
import "../../global.css";
import { AddCity, GetCities, DeleteCity, UpdateCity } from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const City = () => {
  document.title = "Cities | Susipizza";

  const [modal, setModal] = useState(false);
  const [cityName, setCityName] = useState("");
  const [cityId, setCityId] = useState("");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);

  const toggle = () => {
    if (modal) {
      setCityName("");
      setCityId("");
      setEditingId(null);
    }
    setModal(!modal);
  };

  const handleCityNameChange = (e) => {
    const value = e.target.value;
    setCityName(value);
    if (!editingId) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setCityId(slug);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      const resp = await GetCities();
      setCities(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Unable to load cities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (editingId) {
        await UpdateCity(token, editingId, { name: cityName, cityId: editingId });
        toast.success("City updated successfully");
      } else {
        await AddCity(token, { name: cityName, cityId: cityId });
        toast.success("City added successfully");
      }
      setCityName("");
      setCityId("");
      setEditingId(null);
      toggle();
      getData();
    } catch (err) {
      console.error(err);
      const validationErrs = err.response?.data?.validationErrors;
      if (validationErrs && Array.isArray(validationErrs)) {
        validationErrs.forEach(e => toast.error(`${e.field}: ${e.message}`));
      } else {
        const errorMsg = err.response?.data?.error?.message || err.response?.data?.error || "Error occurred while saving city";
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeleteCity(token, targetId);
      toast.success("City removed successfully");
      getData();
    } catch (err) {
      console.error(err);
      toast.error("Error occurred while removing city");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id || row._id);
    setCityName(row.name || "");
    setCityId(row.cityId || row.id || row._id);
    setModal(true);
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">SR No.</span>,
      selector: (row, index) => (index + 1).toString(),
    },
    {
      name: <span className="font-weight-bold fs-13">City ID</span>,
      selector: (row) => row.id || row._id,
      width: "220px",
    },
    {
      name: <span className="font-weight-bold fs-13">City</span>,
      selector: (row) => row.name,
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      width: "110px",
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <Button
            color="primary"
            outline
            className="btn-icon"
            onClick={() => startEdit(row)}
            title="Edit City"
          >
            <i className="mdi mdi-pencil"></i>
          </Button>
          <Button
            color="danger"
            outline
            className="btn-icon"
            onClick={() => handleDeleteClick(row.id || row._id)}
            title="Remove City"
          >
            <i className="mdi mdi-trash-can-outline"></i>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Cities" />
          <Card>
            <CardBody>
              <Row className="align-items-center mb-3">
                <Col>
                  <h4 className="card-title mb-0">Cities</h4>
                  <p className="card-title-desc">Manage the cities available for promotions and stores.</p>
                </Col>
                <Col className="text-end">
                  <Button color="primary" onClick={toggle}>
                    Add City
                  </Button>
                </Col>
              </Row>
              <AlternativePagination columns={columns} data={cities} />
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>{editingId ? "Edit City" : "Add City"}</ModalHeader>
        <ModalBody className="modal-color">
          <FormGroup>
            <Label for="cityName">City Name</Label>
            <Input
              id="cityName"
              name="cityName"
              placeholder="Enter city name"
              value={cityName}
              onChange={handleCityNameChange}
            />
          </FormGroup>
          <FormGroup>
            <Label for="cityIdInput">City ID (System Code)</Label>
            <Input
              id="cityIdInput"
              name="cityId"
              placeholder="e.g. ranchi"
              value={cityId}
              onChange={(e) => setCityId(e.target.value.replace(/[^a-zA-Z0-9-]/g, ""))}
              disabled={!!editingId}
              required
            />
            <small className="text-muted">
              Must be unique. Alphanumeric and hyphens only. Used for referencing in system logic.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            Save
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this city? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>Delete</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default City;
